/**
 * RXF Service — Radiologist Experience Framework API client.
 * Covers Phase 1 (template preview), Phase 2 (template builder),
 * Phase 3 (prior report comparison), Phase 4 (reporting profiles).
 */
import apiClient from './apiClient';

const BASE = '/api/v1/rxf';

// ── Phase 1 — Template Preview ────────────────────────────────────────

export interface TemplateSectionPreview {
  name: string;
  default_text?: string;
  is_required: boolean;
}

export interface TemplatePreviewResponse {
  cpt_code?: string;
  exam_name?: string;
  modality?: string;
  body_region?: string;
  sections: TemplateSectionPreview[];
  has_grading: boolean;
  preview_source: string;
}

export const getTemplatePreview = async (params: {
  cpt_code?: string;
  modality?: string;
  body_region?: string;
}): Promise<TemplatePreviewResponse> => {
  const res = await apiClient.get(`${BASE}/templates/preview`, { params });
  return res.data;
};

// ── Phase 2 — Radiologist Template Builder ────────────────────────────

export interface RadiologistTemplate {
  id: number;
  name: string;
  description?: string;
  modality?: string;
  body_region?: string;
  cpt_code?: string;
  natural_language_spec: string;
  generated_prompt: string;
  status: string;
  version: number;
  created_at?: string;
  updated_at?: string;
}

export interface GenerateTemplateRequest {
  natural_language_spec: string;
  modality?: string;
  body_region?: string;
  name?: string;
}

export interface GenerateTemplateResponse {
  generated_prompt: string;
  structure_json?: Array<{ name: string; instruction?: string }>;
  detected_modality?: string;
  detected_body_region?: string;
}

export const generateTemplatePreview = async (
  req: GenerateTemplateRequest,
): Promise<GenerateTemplateResponse> => {
  const res = await apiClient.post(`${BASE}/templates/generate-preview`, req);
  return res.data;
};

export const saveRadiologistTemplate = async (data: {
  name: string;
  description?: string;
  natural_language_spec: string;
  generated_prompt: string;
  structure_json?: unknown[];
  modality?: string;
  body_region?: string;
  cpt_code?: string;
}): Promise<RadiologistTemplate> => {
  const res = await apiClient.post(`${BASE}/templates`, data);
  return res.data;
};

export const listRadiologistTemplates = async (): Promise<RadiologistTemplate[]> => {
  const res = await apiClient.get(`${BASE}/templates`);
  return res.data;
};

export const updateRadiologistTemplate = async (
  id: number,
  data: Partial<RadiologistTemplate> & { change_note?: string },
): Promise<RadiologistTemplate> => {
  const res = await apiClient.put(`${BASE}/templates/${id}`, data);
  return res.data;
};

export const rollbackTemplate = async (id: number, version: number): Promise<void> => {
  await apiClient.post(`${BASE}/templates/${id}/rollback/${version}`);
};

export const submitTemplateForApproval = async (id: number): Promise<void> => {
  await apiClient.post(`${BASE}/templates/${id}/submit`);
};

export const archiveTemplate = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE}/templates/${id}`);
};

// ── Phase 3 — Prior Report Comparison ────────────────────────────────

export interface PriorReportAttachment {
  id: number;
  session_id: string;
  source: 'uploaded' | 'patient_history';
  original_filename?: string;
  exam_date?: string;
  exam_type?: string;
  extraction_status: 'pending' | 'done' | 'failed';
  has_structured_findings: boolean;
}

export const uploadPriorReport = async (
  sessionId: string,
  file: File,
): Promise<PriorReportAttachment> => {
  const form = new FormData();
  form.append('file', file);
  const res = await apiClient.post(`${BASE}/prior-reports/upload?session_id=${sessionId}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const attachPriorReportText = async (
  sessionId: string,
  text: string,
  examDate?: string,
  examType?: string,
): Promise<PriorReportAttachment> => {
  const res = await apiClient.post(`${BASE}/prior-reports/text`, {
    session_id: sessionId,
    text,
    exam_date: examDate,
    exam_type: examType,
  });
  return res.data;
};

export const autoFetchPriorReport = async (
  sessionId: string,
): Promise<PriorReportAttachment | null> => {
  const res = await apiClient.get(`${BASE}/prior-reports/auto-fetch/${sessionId}`);
  return res.data;
};

export const getSessionPriorReport = async (
  sessionId: string,
): Promise<PriorReportAttachment | null> => {
  const res = await apiClient.get(`${BASE}/prior-reports/${sessionId}`);
  return res.data;
};

export const deletePriorReport = async (attachmentId: number): Promise<void> => {
  await apiClient.delete(`${BASE}/prior-reports/${attachmentId}`);
};

// ── Phase 4 — Reporting Profiles ─────────────────────────────────────

export interface ReportingProfile {
  id: number;
  name: string;
  description?: string;
  is_org_default: boolean;
  report_length?: string;
  impression_style?: string;
  rec_style?: string;
  bold_positives?: boolean;
  max_impression_points?: number;
  custom_instructions?: string;
  target_modalities?: string[];
  owner_user_id?: number;
}

export interface ReportingProfileRequest {
  name: string;
  description?: string;
  report_length?: string;
  impression_style?: string;
  rec_style?: string;
  bold_positives?: boolean;
  max_impression_points?: number;
  custom_instructions?: string;
  target_modalities?: string[];
}

export const listReportingProfiles = async (): Promise<ReportingProfile[]> => {
  const res = await apiClient.get(`${BASE}/profiles`);
  return res.data;
};

export const createReportingProfile = async (
  data: ReportingProfileRequest,
): Promise<ReportingProfile> => {
  const res = await apiClient.post(`${BASE}/profiles`, data);
  return res.data;
};

export const updateReportingProfile = async (
  id: number,
  data: ReportingProfileRequest,
): Promise<ReportingProfile> => {
  const res = await apiClient.put(`${BASE}/profiles/${id}`, data);
  return res.data;
};

export const activateReportingProfile = async (id: number): Promise<void> => {
  await apiClient.put(`${BASE}/profiles/${id}/activate`);
};

export const deleteReportingProfile = async (id: number): Promise<void> => {
  await apiClient.delete(`${BASE}/profiles/${id}`);
};

export const updateOrgReportingProfile = async (
  id: number,
  data: ReportingProfileRequest,
): Promise<ReportingProfile> => {
  const res = await apiClient.put(`${BASE}/admin/profiles/${id}`, data);
  return res.data;
};

// ── Phase 1 — Reporting preferences (via user-preferences endpoint) ───

export interface ReportingPreferences {
  report_length: string;
  impression_style: string;
  rec_style: string;
  bold_positives: boolean;
  max_impression_points: number;
  active_profile_id?: number;
}

export const updateReportingPreferences = async (
  prefs: Partial<ReportingPreferences>,
): Promise<void> => {
  await apiClient.put('/api/v1/user-preferences/reporting', prefs);
};
